import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled, { createGlobalStyle } from 'styled-components'
import { em } from 'polished'
import cloneDeep from 'lodash-es/cloneDeep'
import moment from 'moment'
import { Toast, createToast } from '@binance-chain/honeycomb'

import { setTriggerElement, setLoading } from '../../redux/global/action'
import { setNeedAuth } from '../../redux/account/action'
import { setWalletConnectURI } from '../../redux/walletConnect/action'
import cancelIcon from '../../static/images/cancelblack.svg'

import { Consumer as BNCClientConsumer } from '../../containers/context/BNCClientProvider'
import { Consumer as WalletConnectClientConsumer } from '../../containers/context/WalletConnectProvider'
import CheckBox from '../common/CheckBox'
import Icon from '../common/Icon'
import FontNumber from '../common/FontNumber'
import ScrollableTable from './ScrollableTable'
import TxHash from '../order/TxHash'
import Side from '../order/Side'
import { pencentage, roundValue } from '../../utils/number'
import hasPrivateKey, { isUnlockWithWalletConnect } from '../../utils/account'
import LoadingShade from '../common/LoadingShade'
import Confirm from '../common/Confirm'
import message from '../common/Message'
import httpRequest from '../../utils/httpRequest'
import reduceTradePairBSufix from '../../utils/tradepair'
import { withI18n } from '../../i18n/util'

const Header = styled.div`
  height: 30px;
  padding: 0 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.exchangeFilterBg};
  overflow: hidden;
`

const HideOtherPairs = styled.div`
  position: relative;
`

const CancelWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const CancelButton = styled.button`
  height: 22px;
  line-height: 20px;
  border-radius: 3px;
  text-align: center;
  padding: 0 10px;
  color: #848e9c;
  margin-left: 4px;
  background: ${({ theme }) => theme.inputBgColor};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.exchangeButtonBorderColor};
  outline: none;
  &:hover {
    background: ${({ theme }) => theme.exchangeButtonHoverBg};
    border-color: ${({ theme }) => theme.inputBgColor};
    color: ${({ theme }) => theme.highlight};
  }
`

const Content = styled.div`
  margin-top: 10px;
  height: 200px;
  padding: 0 6px;
  overflow: hidden;
  position: relative;
  .table-wrap {
    .fixed-table {
      bottom: 20px;
    }
  }
  table tbody tr {
    padding: 0;
    height: 25px;
    line-height: 25px;
    &:nth-child(odd) {
      background: ${({ theme }) => theme.tableOddRowBg};
    }
  }
`

export const CellItem = styled.div`
  color: #848e9c;
  text-align: left;
`

export const CellItemRight = styled.div`
  color: #848e9c;
  text-align: right;
`

export const OpItem = styled(CellItem)`
  text-align: right;
`

export const FilledItem = styled.div`
  color: ${props => (props.fill ? '#848E9C' : '#48515D')};
  text-align: right;
`

export const DateItem = styled.div`
  color: #48515d;
  text-align: left;
`

const ConfirmContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  img {
    width: 100px;
    margin-bottom: 20px;
  }
`

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ToastDescription = styled.p`
  display: flex;
  font-size: ${({ theme }) => em(theme.honeycomb.size.normal)};
  justify-content: center;
`

const GlobalStyles = createGlobalStyle`
  .Toastify__toast-container--top-right {
    top: 6em;
  }

  .Toastify__toast-container {
    max-width: 25.25em;
  }
`

class OpenOrders extends PureComponent {
  static propTypes = {
    openOrders: PropTypes.array,
    currentTrade: PropTypes.string,
    total: PropTypes.number,
    getOpenOrders: PropTypes.func,
    dispatch: PropTypes.func,
    isFetching: PropTypes.bool,
    address: PropTypes.string,
    privateKey: PropTypes.string,
    userInfo: PropTypes.object,
    formatI18nText: PropTypes.func,
    isExtensionWallet: PropTypes.bool,
  }

  static defaultProps = {
    openOrders: [],
    currentTrade: '',
    address: '',
    privateKey: '',
    isFetching: false,
    userInfo: {},
    total: 0,
    getOpenOrders: () => {},
    dispatch: () => {},
    formatI18nText: () => {},
    isExtensionWallet: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      hidePairs: false,
      isConfirm: false,
      cancelText: '',
      toCancelItem: {},
      cancelled: new Set(),
    }
  }

  _handleCheckChange = e => {
    const { getOpenOrders, currentTrade } = this.props
    const {
      target: { checked },
    } = e
    this.setState({ hidePairs: checked }, () => {
      const opts = {}
      if (checked) {
        opts.symbol = currentTrade
      }
      getOpenOrders && getOpenOrders(opts)
    })
  }

  _buildColumns = wcClient => {
    const { formatI18nText } = this.props
    const columns = [
      {
        title: formatI18nText('exchange.txHash'),
        headerAlign: 'left',
        dataIndex: 'txHash',
        key: 'TxHash',
        width: '20%',
        render: item => (
          <div>
            <TxHash hash={item.txHash} />
          </div>
        ),
      },
      {
        title: formatI18nText('exchange.date'),
        dataIndex: 'date',
        headerAlign: 'left',
        key: 'Date',
        width: '13%',
        render: item => (
          <CellItem>
            <FontNumber value={moment(item.date).format('MM-DD HH:mm:ss')} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.pair'),
        dataIndex: 'pair',
        headerAlign: 'left',
        key: 'Pair',
        width: '12%',
        render: item => <CellItem>{reduceTradePairBSufix(item.pair)}</CellItem>,
      },
      {
        title: formatI18nText('exchange.side'),
        dataIndex: 'side',
        headerAlign: 'left',
        key: 'Side',
        width: '5%',
        render: item => <Side text={item.side} textAlign="left" />,
      },
      {
        title: formatI18nText('exchange.price'),
        dataIndex: 'price',
        headerAlign: 'left',
        key: 'Price',
        width: '13%',
        render: item => (
          <CellItem>
            <FontNumber value={item.price} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.amount'),
        dataIndex: 'amount',
        headerAlign: 'left',
        key: 'Amount',
        width: '12%',
        render: item => (
          <CellItem>
            <FontNumber value={item.amount} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.filled'),
        dataIndex: 'filled',
        headerAlign: 'right',
        key: 'Filled',
        width: '5%',
        render: item => (
          <CellItemRight fill={item.cumulateQuantity === 0}>
            <FontNumber value={item.filled} />
          </CellItemRight>
        ),
      },
      {
        title: formatI18nText('exchange.notional'),
        dataIndex: 'total',
        headerAlign: 'right',
        key: 'Notional',
        width: '17%',
        render: item => (
          <CellItemRight>
            <FontNumber value={item.total} />
          </CellItemRight>
        ),
      },
      {
        title: '',
        dataIndex: 'op',
        key: 'Total',
        width: '3%',
        render: item =>
          hasPrivateKey() ? (
            <OpItem
              onClick={ev => {
                this._cancelOne(item, ev, wcClient)
              }}
            >
              <Icon type="close" size="12" />
            </OpItem>
          ) : null,
      },
    ]
    return columns
  }

  _cancel = async (client, items) => {
    const {
      address,
      privateKey,
      currentTrade,
      getOpenOrders,
      dispatch,
      formatI18nText,
      isExtensionWallet,
    } = this.props
    const account = (await httpRequest.getAccount(address)) || {}

    privateKey !== 'HARDWARE' && (await client.setPrivateKey(privateKey))

    try {
      if (!isExtensionWallet) dispatch(setLoading(true))

      let msg = formatI18nText('openOrders.cancelSuccess')

      const newCancelled = new Set()
      for (let i = 0, len = items.length; i < len; i++) {
        const { symbol, pair, orderId } = items[i]
        const res = await client.cancelOrder(address, symbol || pair, orderId, account.sequence + i)

        if (res) {
          newCancelled.add(orderId)
        } else {
          msg = formatI18nText('openOrders.cancelPartialSuccess')
          break
        }
      }

      dispatch(setLoading(false))
      this.setState(({ cancelled }) => ({
        isConfirm: false,
        cancelled: new Set([...cancelled, ...newCancelled]),
      }))
      createToast(
        <Toast icon={<Toast.Icon.Success />} data-testid="success-toast">
          <ToastContainer>
            <ToastDescription>{msg}</ToastDescription>
          </ToastContainer>
        </Toast>
      )
      this.timeoutHandle = setTimeout(() => {
        const { hidePairs } = this.state
        const opts = {}
        if (hidePairs) {
          opts.symbol = currentTrade
        }
        getOpenOrders && getOpenOrders(opts)
      }, 1500)
    } catch (err) {
      console.log(err)
      dispatch(setLoading(false))
      createToast(
        <Toast icon={<Toast.Icon.Danger />} data-testid="danger-toast">
          <ToastContainer>
            <ToastDescription>
              {err.message || err || formatI18nText('common.networkError')}
            </ToastDescription>
          </ToastContainer>
        </Toast>
      )
      this.setState({
        isConfirm: false,
      })
    }
  }

  _buildDataSource = () => {
    const { openOrders } = this.props
    const result = []
    openOrders.forEach(order => {
      const { orderId } = order
      const { cancelled } = this.state
      if (!cancelled.has(orderId)) {
        result.push({
          txHash: order.transactionHash,
          date: order.orderCreateTime,
          pair: order.symbol,
          side: order.side === 1 ? 'Buy' : 'Sell',
          price: roundValue(order.price),
          amount: roundValue(order.quantity),
          cumulateQuantity: order.cumulateQuantity,
          filled: pencentage(order.cumulateQuantity, order.quantity),
          total: roundValue(order.price * order.quantity),
          op: '',
          orderId,
        })
      }
    })
    return result
  }

  _walletConnectCheck = async (wcClient, currentTarget) => {
    const { dispatch } = this.props
    if (isUnlockWithWalletConnect() && !wcClient.connected()) {
      const uri = await wcClient.startSession(false)
      console.log('WalletConnect URI', uri)
      dispatch(setWalletConnectURI(uri))
      dispatch(setTriggerElement(currentTarget))
      return true
    }
    return false
  }

  _cancelAll = wcClient => async ev => {
    const { currentTarget } = ev
    const { privateKey, openOrders, dispatch, formatI18nText } = this.props

    if (openOrders.length === 0) {
      message.warn(formatI18nText('openOrders.noOpenOrders'))
      return
    }

    if (await this._walletConnectCheck(wcClient, currentTarget)) return

    if (!privateKey) {
      dispatch(setTriggerElement(currentTarget))
      dispatch(setNeedAuth(true))
      return
    }

    this.setState({ isConfirm: true, cancelText: 'cancel all' })
  }

  _cancelBuy = wcClient => async ev => {
    const { currentTarget } = ev
    const { privateKey, openOrders, dispatch, formatI18nText } = this.props
    const openBuyOrders = openOrders.filter(item => item.side === 1)
    if (openBuyOrders.length === 0) {
      message.warn(formatI18nText('openOrders.noBuyOpenOrders'))
      return
    }

    if (await this._walletConnectCheck(wcClient, currentTarget)) return

    if (!privateKey) {
      dispatch(setTriggerElement(currentTarget))
      dispatch(setNeedAuth(true))
      return
    }

    this.setState({ isConfirm: true, cancelText: 'cancel buy' })
  }

  _cancelSell = wcClient => async ev => {
    const { currentTarget } = ev
    const { privateKey, openOrders, dispatch, formatI18nText } = this.props
    const openSellOrders = openOrders.filter(item => item.side === 2)
    if (openSellOrders.length === 0) {
      message.warn(formatI18nText('openOrders.noSellOpenOrders'))
      return
    }
    if (await this._walletConnectCheck(wcClient, currentTarget)) return

    if (!privateKey) {
      dispatch(setTriggerElement(currentTarget))
      dispatch(setNeedAuth(true))
      return
    }

    this.setState({ isConfirm: true, cancelText: 'cancel sell' })
  }

  _cancelOne = async (item, ev, wcClient) => {
    const { currentTarget } = ev
    const { privateKey, dispatch } = this.props
    if (await this._walletConnectCheck(wcClient, currentTarget)) return

    if (!privateKey) {
      dispatch(setTriggerElement(currentTarget))
      dispatch(setNeedAuth(true))
      return
    }

    this.setState({ isConfirm: true, cancelText: 'cancel', toCancelItem: item })
  }

  _confirmCancel = client => () => {
    const { cancelText, toCancelItem } = this.state
    const { openOrders } = this.props
    if (cancelText === 'cancel all') {
      this._cancel(client, openOrders)
    } else if (cancelText === 'cancel buy') {
      const openBuyOrders = openOrders.filter(item => item.side === 1)
      this._cancel(client, openBuyOrders)
    } else if (cancelText === 'cancel sell') {
      const openSellOrders = openOrders.filter(item => item.side === 2)
      this._cancel(client, openSellOrders)
    } else {
      this._cancel(client, [toCancelItem])
    }
  }

  _hide = () => {
    this.setState({ toCancelItem: {}, isConfirm: false })
  }

  render() {
    const { hidePairs, cancelText, isConfirm } = this.state
    const { isFetching, formatI18nText } = this.props
    const dataSource = this._buildDataSource()
    return (
      <>
        <GlobalStyles />
        <WalletConnectClientConsumer>
          {wcClient => (
            <BNCClientConsumer>
              {bncClient => (
                <div>
                  <Header>
                    <HideOtherPairs>
                      <CheckBox
                        labelTitle={formatI18nText('exchange.hideOtherPairs')}
                        defaultChecked={hidePairs}
                        handleCheckChange={this._handleCheckChange}
                      />
                    </HideOtherPairs>
                    {hasPrivateKey() && (
                      <CancelWrapper>
                        <CancelButton onClick={this._cancelAll(wcClient)} id="Cancel All">
                          {formatI18nText('openOrders.cancelAll')}
                        </CancelButton>
                        <CancelButton onClick={this._cancelBuy(wcClient)} id="Cancel Buy">
                          {formatI18nText('openOrders.cancelBuy')}
                        </CancelButton>
                        <CancelButton onClick={this._cancelSell(wcClient)} id="Cancel Sell">
                          {formatI18nText('openOrders.cancelSell')}
                        </CancelButton>
                      </CancelWrapper>
                    )}
                  </Header>
                  <Content>
                    <ScrollableTable
                      columns={this._buildColumns(wcClient)}
                      dataSource={dataSource}
                      isFixedHeader={true}
                    />
                    {isFetching && <LoadingShade />}
                    {isConfirm && (
                      <Confirm
                        cancelText={
                          cancelText === 'cancel'
                            ? formatI18nText('openOrders.keepOrder')
                            : formatI18nText('openOrders.keepOrders')
                        }
                        okText={
                          cancelText === 'cancel'
                            ? formatI18nText('openOrders.cancelOrder')
                            : formatI18nText('openOrders.cancelOrders')
                        }
                        confirm={this._confirmCancel(bncClient)}
                        cancel={this._hide}
                      >
                        <ConfirmContent>
                          <img src={cancelIcon} alt={true} />
                          <p>
                            {`${formatI18nText('openOrders.areYourTrue')} ${formatI18nText(
                              `openOrders.${cancelText}`
                            )}`}
                            {'?'}
                          </p>
                        </ConfirmContent>
                      </Confirm>
                    )}
                  </Content>
                </div>
              )}
            </BNCClientConsumer>
          )}
        </WalletConnectClientConsumer>
      </>
    )
  }
}

const mapStateToProps = ({
  exchange: { currentTrade },
  account: {
    privateKey,
    address,
    userInfo,
    flags: { isExtensionWallet },
  },
  order: { openOrders, isFetchOpenOrders },
}) => ({
  openOrders: cloneDeep(openOrders.order),
  total: openOrders.total,
  isFetching: isFetchOpenOrders,
  currentTrade,
  privateKey,
  address,
  userInfo,
  isExtensionWallet,
})

export default withI18n(connect(mapStateToProps)(OpenOrders))
